import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Typography, Box, TextField, Button,
  alpha, InputAdornment, Tooltip, IconButton, Paper, CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon, Business as CompanyIcon,
  Person as PersonIcon, Phone as PhoneIcon, Email as EmailIcon,
  Language as WebIcon, LocationOn as LocationIcon,
  DriveFileRenameOutline as NameIcon,
  Share as SocialIcon,
  Image as ImageIcon,
  Flag as FlagIcon,
  Description as NotesIcon,
  CloudUpload as UploadIcon, Delete as DeleteIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Link as LinkIcon,
  AutoAwesome as ImportWebIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import SearchableSelect from '../../common/SearchableSelect';
import { SUCCESS } from '../../common/SaaSTokens';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import StorageImage from '../../common/StorageImage';
import {
  applyApiValidationErrors,
  createFieldErrorGate,
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
} from '../../../utils/formValidationFeedback';

// ─── Tokens ───────────────────────────────────────────────────────────────────
const NEON      = '#38BDF8';
const NEON3     = '#34D399';
const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';
const TEXT_BODY    = '#E2E8F0';
const MEDIA_FRAME_HEIGHT = 220;
const mediaFrameSx = {
  width: '100%',
  height: MEDIA_FRAME_HEIGHT,
  minHeight: MEDIA_FRAME_HEIGHT,
  maxHeight: MEDIA_FRAME_HEIGHT,
  boxSizing: 'border-box',
  borderRadius: '12px',
};

const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  .MuiOutlinedInput-input, .MuiInputBase-input { color: #E2E8F0 !important; -webkit-text-fill-color: #E2E8F0 !important; caret-color: #38BDF8 !important; }
  .MuiOutlinedInput-input:-webkit-autofill, .MuiInputBase-input:-webkit-autofill { -webkit-box-shadow: 0 0 0 100px #0f1f38 inset !important; -webkit-text-fill-color: #E2E8F0 !important; }
  .MuiFormLabel-asterisk { color: #EF4444 !important; }
`;

const MotionPaper = motion(Paper);

const monoSx = (color = alpha(NEON, 0.55), size = '0.58rem') => ({
  fontFamily: "'JetBrains Mono', monospace", fontSize: size,
  fontWeight: 600, letterSpacing: 1.8, textTransform: 'uppercase', color, lineHeight: 1,
});

const SectionCard = ({ title, icon, delay = 0, children, accentNeon }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay} accentNeon={accentNeon}>
    {children}
  </CommonSectionCard>
);

const inputRootSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem',
    color: alpha(TEXT_BODY, 0.85), background: alpha(SURFACE_UP, 0.7), backdropFilter: 'blur(8px)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': { borderColor: alpha(NEON, 0.18) },
    '&:hover fieldset': { borderColor: alpha(NEON, 0.4) },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}`, animation: 'form-glow-in 0.3s ease' },
    '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
    '&.Mui-disabled': { background: alpha(SURFACE_UP, 0.4), '& fieldset': { borderColor: alpha(NEON, 0.08) } },
  },
  '& .MuiInputBase-input': {
    color: `${alpha(TEXT_BODY, 0.85)} !important`,
    WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important`,
    '&::placeholder': { color: alpha(NEON, 0.3), opacity: 1 },
  },
  '& .MuiInputLabel-root': { fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45) },
  '& label.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 },
  '& .MuiFormLabel-asterisk': { color: '#EF4444', fontWeight: 700 },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.45), fontSize: 18 },
  '& .MuiFormHelperText-root': { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: alpha(NEON, 0.5) },
  '& .MuiFormHelperText-root.Mui-error': { color: '#F87171' },
};

const StyledField = ({ sx, ...props }) => <TextField {...props} sx={{ ...inputRootSx, ...sx }} />;

const iconAdorn = (Icon) => (
  <InputAdornment position="start"><Icon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment>
);
const topIconAdorn = (Icon) => (
  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
    <Icon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />
  </InputAdornment>
);

const UploadZone = ({ id, label, icon: Icon, hideFileInput = false, onDrop, onDragOver }) => (
  <Paper
    component={hideFileInput ? 'div' : 'label'}
    htmlFor={hideFileInput ? undefined : id}
    onDrop={onDrop} onDragOver={onDragOver}
    sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      ...mediaFrameSx,
      p: 2.5, cursor: 'pointer',
      border: `2px dashed ${alpha(NEON, 0.28)}`,
      background: alpha(SURFACE_UP, 0.4), transition: 'all 0.28s ease',
      '&:hover': { borderColor: alpha(NEON, 0.6), background: alpha(NEON, 0.04), boxShadow: `0 0 20px ${alpha(NEON, 0.15)}` },
    }}
  >
    <Icon sx={{ fontSize: 28, color: alpha(NEON, 0.55), mb: 0.75 }} />
    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: alpha(TEXT_BODY, 0.75), textAlign: 'center' }}>{label}</Typography>
    <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(NEON, 0.4), mt: 0.5 }}>Click or drag & drop</Typography>
    {!hideFileInput && <input type="file" id={id} style={{ display: 'none' }} />}
  </Paper>
);

function appendIfNonEmpty(fd, key, val) {
  const t = typeof val === 'string' ? val.trim() : val;
  if (t) fd.append(key, t);
}

// ─── Status options ───────────────────────────────────────────────────────────
const PIPELINE_STATUS_OPTIONS = [
  { value: 'follow_up', label: 'Follow up' },
  { value: 'active',    label: 'Active'    },
  { value: 'inactive',  label: 'Inactive'  },
];

const WEBSITE_PREVIEW_FIELDS = [
  'name', 'description', 'location', 'gmail', 'contact_number',
  'facebook_path', 'ig_path', 'youtube_path', 'other_path',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const PHONE_PATTERN = /^[\d\-+()\s]+$/;
const URL_RE = /^(https?:\/\/)?([\w.-]+\.)+[\w.-]{2,}(\/[^\s]*)?$/i;

const VALIDATED_FIELDS = [
  'name', 'contact_number', 'gmail', 'website_link', 'location',
  'person_in_charge_name', 'person_in_charge_email', 'person_in_charge_phone',
  'description', 'facebook_path', 'ig_path', 'youtube_path', 'other_path',
  'user_ids', 'status',
];

const COMPANY_VALIDATION_FIELDS = new Set(VALIDATED_FIELDS);

const validateCompanyField = (name, value) => {
  const v = typeof value === 'string' ? value.trim() : value;
  switch (name) {
    case 'name':
      if (!v) return 'Company name is required.';
      return '';
    case 'contact_number':
      if (!v) return 'Contact number is required.';
      if (!PHONE_PATTERN.test(v)) return 'Invalid phone number.';
      return '';
    case 'gmail':
      if (!v) return 'Company email is required.';
      if (!EMAIL_RE.test(v)) return 'Enter a valid email address.';
      return '';
    case 'website_link':
      if (!v) return '';
      if (!URL_RE.test(v)) return 'Enter a valid website URL (e.g. https://example.com).';
      return '';
    case 'person_in_charge_email':
      if (!v) return '';
      if (!EMAIL_RE.test(v)) return 'Enter a valid email address.';
      return '';
    case 'person_in_charge_phone':
      if (!v) return '';
      if (!PHONE_PATTERN.test(v)) return 'Invalid phone number.';
      return '';
    case 'facebook_path':
    case 'ig_path':
    case 'youtube_path':
    case 'other_path':
      if (!v) return '';
      if (!URL_RE.test(v)) return 'Enter a valid URL.';
      return '';
    case 'user_ids':
      if (!Array.isArray(value) || value.length === 0) return 'Please select at least one assigned member.';
      return '';
    case 'status':
      if (!v) return 'Please select a pipeline status.';
      if (!['follow_up', 'active', 'inactive'].includes(v)) return 'Invalid pipeline status.';
      return '';
    default:
      return '';
  }
};

const collectValidationErrors = (form) => {
  const errors = {};
  VALIDATED_FIELDS.forEach((key) => {
    const msg = validateCompanyField(key, form[key]);
    if (msg) errors[key] = msg;
  });
  return errors;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CompanyForm = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id }   = useParams();
  const { user: currentUser } = useAuth();

  const [loading,       setLoading]       = useState(false);
  const [dataLoading,   setDataLoading]   = useState(isEdit);
  const [error,         setError]         = useState(null);
  const [success,       setSuccess]       = useState(null);
  const [confirmOpen,   setConfirmOpen]   = useState(false);
  const [users,         setUsers]         = useState([]);
  const [logoFile,      setLogoFile]      = useState(null);
  const [coverFile,     setCoverFile]     = useState(null);
  const [existingLogo,  setExistingLogo]  = useState(null);
  const [existingCover, setExistingCover] = useState(null);
  const [logoPreview,   setLogoPreview]   = useState(null);
  const [coverPreview,  setCoverPreview]  = useState(null);
  const [logoRemoved,   setLogoRemoved]   = useState(false);
  const [coverRemoved,  setCoverRemoved]  = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const [formData, setFormData] = useState({
    name: '', contact_number: '', gmail: '', website_link: '', location: '',
    user_ids: [], status: 'follow_up', description: '',
    person_in_charge_name: '', person_in_charge_email: '', person_in_charge_phone: '',
    facebook_path: '', ig_path: '', youtube_path: '', other_path: '',
  });

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'company',
    userId: currentUser?.id,
    isEditMode: isEdit,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  useEffect(() => {
    const cssId = 'companyform-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style'); el.id = cssId; el.innerHTML = FORM_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    (async () => {
      try {
        const r = await api.get('/users?active_only=1');
        setUsers(Array.isArray(r.data) ? r.data : []);
      } catch { setUsers([]); }
    })();
    if (isEdit && id) fetchCompany();
    else setDataLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, id, isEdit]);

  const fetchCompany = async () => {
    try {
      setDataLoading(true);
      const { data: c } = await api.get(`/companies/${id}`);
      setFormData({
        name: c.name || '', contact_number: c.contact_number || '', gmail: c.gmail || '',
        website_link: c.website_link || '', location: c.location || '',
        user_ids: (c.assignees || []).map((member) => String(member.id)),
        status: ['follow_up', 'active', 'inactive'].includes(c.status) ? c.status : 'follow_up',
        description: c.description || '',
        person_in_charge_name: c.person_in_charge_name || '',
        person_in_charge_email: c.person_in_charge_email || '',
        person_in_charge_phone: c.person_in_charge_phone || '',
        facebook_path: c.facebook_path || '', ig_path: c.ig_path || '',
        youtube_path: c.youtube_path || '', other_path: c.other_path || '',
      });
      setExistingLogo(c.company_logo || null);
      setExistingCover(c.company_cover_picture || null);
      setLogoFile(null);
      setCoverFile(null);
      setLogoPreview(null);
      setCoverPreview(null);
      setLogoRemoved(false);
      setCoverRemoved(false);
      setValidationErrors({});
    } catch { setError('Failed to load company details.'); navigate('/companies'); }
    finally { setDataLoading(false); }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleNativeChange = (e) => handleChange(e.target.name, e.target.value);

  const mergeWebsitePreview = (preview) => {
    if (!preview || typeof preview !== 'object') return 0;
    let filled = 0;
    setFormData((prev) => {
      const next = { ...prev };
      WEBSITE_PREVIEW_FIELDS.forEach((key) => {
        const incoming = typeof preview[key] === 'string' ? preview[key].trim() : '';
        if (!incoming || (prev[key] || '').trim()) return;
        next[key] = incoming;
        filled += 1;
      });
      return next;
    });
    return filled;
  };

  const handleImportFromWebsite = async () => {
    const url = formData.website_link?.trim();
    if (!url) {
      setError('Enter a website URL first, then import public details.');
      return;
    }
    try {
      setImportLoading(true);
      setError(null);
      const { data: res } = await api.post(
        '/companies/preview-from-url',
        { url },
        { timeout: 30000 },
      );
      const preview = res?.data ?? {};
      const filled = mergeWebsitePreview(preview);
      if (filled > 0) {
        setSuccess(res?.message || `Imported ${filled} field(s). Please review before saving.`);
      } else {
        setError(res?.message || 'No empty fields could be filled from that website.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not import details from that website.';
      setError(msg);
    } finally {
      setImportLoading(false);
    }
  };

  const makeFileHandler = (setFile, setPreview, onSelected) => (e) => {
    const file = e.target?.files?.[0] ?? e.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError('Image must be less than 2MB'); return; }
    if (!file.type.startsWith('image/')) { setError('Please select a valid image file'); return; }
    onSelected?.();
    setFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
    setError(null);
  };

  const handleLogoChange  = makeFileHandler(setLogoFile, setLogoPreview, () => setLogoRemoved(false));
  const handleCoverChange = makeFileHandler(setCoverFile, setCoverPreview, () => setCoverRemoved(false));
  const handleLogoDrop    = (e) => { e.preventDefault(); handleLogoChange(e); };
  const handleCoverDrop   = (e) => { e.preventDefault(); handleCoverChange(e); };
  const preventDropDefaults = (e) => e.preventDefault();

  const removeLogoImage = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const hadExisting = Boolean(existingLogo);
    setLogoFile(null);
    setLogoPreview(null);
    setLogoRemoved(isEdit && hadExisting);
  };

  const removeCoverImage = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const hadExisting = Boolean(existingCover);
    setCoverFile(null);
    setCoverPreview(null);
    setCoverRemoved(isEdit && hadExisting);
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('name', formData.name.trim());
    fd.append('contact_number', formData.contact_number.trim());
    fd.append('gmail', formData.gmail.trim());
    formData.user_ids.forEach((userId) => fd.append('user_ids[]', userId));
    fd.append('status', formData.status);
    appendIfNonEmpty(fd, 'description', formData.description);
    appendIfNonEmpty(fd, 'person_in_charge_name', formData.person_in_charge_name);
    appendIfNonEmpty(fd, 'person_in_charge_email', formData.person_in_charge_email);
    appendIfNonEmpty(fd, 'person_in_charge_phone', formData.person_in_charge_phone);
    appendIfNonEmpty(fd, 'website_link', formData.website_link);
    appendIfNonEmpty(fd, 'location', formData.location);
    appendIfNonEmpty(fd, 'facebook_path', formData.facebook_path);
    appendIfNonEmpty(fd, 'ig_path', formData.ig_path);
    appendIfNonEmpty(fd, 'youtube_path', formData.youtube_path);
    appendIfNonEmpty(fd, 'other_path', formData.other_path);
    if (logoFile) fd.append('company_logo', logoFile);
    if (logoRemoved) fd.append('remove_company_logo', '1');
    if (coverFile) fd.append('company_cover_picture', coverFile);
    if (coverRemoved) fd.append('remove_company_cover_picture', '1');
    return fd;
  };

  const validateForm = () => {
    const errors = collectValidationErrors(formData);
    setValidationErrors(errors);
    setShowFieldErrors(true);
    if (Object.keys(errors).length > 0) {
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEdit) {
      setError(null);
      setValidationErrors({});
      setShowFieldErrors(false);
      setConfirmOpen(true);
      return;
    }
    if (!validateForm()) return;
    doSave();
  };

  const handleConfirmUpdate = () => {
    const errors = collectValidationErrors(formData);
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen: setConfirmOpen,
        setShowFieldErrors,
      });
      return;
    }
    setConfirmOpen(false);
    doSave();
  };

  const doSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});
      const fd = buildFormData();
      if (isEdit) { fd.append('_method', 'PUT'); await api.post(`/companies/${id}`, fd); }
      else        { await api.post('/companies', fd); }
      if (!isEdit) formDraft.clearDraft();
      setSuccess(`Company ${isEdit ? 'updated' : 'created'} successfully!`);
      setTimeout(() => navigate('/companies', { state: { refreshList: Date.now() } }), 1500);
    } catch (err) {
      const data = err?.response?.data;
      applyApiValidationErrors(data, setValidationErrors, COMPANY_VALIDATION_FIELDS);
      const fieldMsgs = {};
      const errs = data?.errors;
      if (errs && typeof errs === 'object') {
        Object.entries(errs).forEach(([key, msgs]) => {
          if (!COMPANY_VALIDATION_FIELDS.has(key)) return;
          const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
          if (msg) fieldMsgs[key] = String(msg);
        });
      }
      if (Object.keys(fieldMsgs).length) setShowFieldErrors(true);
      setError(
        Object.keys(fieldMsgs).length
          ? formatFieldErrorsMessage(fieldMsgs)
          : (getFirstErrorMessage(data) || `Failed to ${isEdit ? 'update' : 'create'} company.`),
      );
    } finally { setLoading(false); }
  };

  if (!currentUser) return null;
  if (dataLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading company form" size={56} variant="primary" />
    </Box>
  );

  const canSubmit = Boolean(
    formData.name?.trim()
    && formData.contact_number?.trim()
    && formData.gmail?.trim()
    && formData.user_ids.length,
  );
  const submitDisabled = loading || !canSubmit;
  const { fieldHelper, fieldHasError } = createFieldErrorGate(showFieldErrors, validationErrors);

  // Build option arrays for SearchableSelect
  const userSelectOptions = [
    ...users.map(u => ({ value: String(u.id), label: u.name || u.email || `User #${u.id}` })),
  ];

  const ImagePreviewBlock = ({ preview, existingPath, uploadId, onRemove, changeTooltip, removeTooltip, altText }) => {
    const src = preview || (isEdit && existingPath ? existingPath : null);
    if (!src) return null;
    const isBlob = String(src).startsWith('data:') || String(src).startsWith('blob:');
    return (
      <MotionPaper
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}
        sx={{
          ...mediaFrameSx,
          position: 'relative',
          overflow: 'hidden',
          background: alpha(SURFACE_UP, 0.6),
          border: `1px solid ${alpha(NEON, 0.22)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& img': {
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
            boxSizing: 'border-box',
          },
        }}
      >
        {isBlob ? (
          <Box component="img" src={src} alt={altText}
            sx={{ p: 1.5 }} />
        ) : (
          <StorageImage storagePath={src} alt={altText}
            sx={{ p: 1.5 }} />
        )}
        <Box sx={{ position: 'absolute', top: 0, right: 0, p: 1, display: 'flex', gap: 1 }}>
          <Tooltip title={changeTooltip}>
            <label htmlFor={uploadId}>
              <IconButton component="span" size="small" sx={{ background: alpha(NEON, 0.15), color: NEON, '&:hover': { background: alpha(NEON, 0.25) } }}>
                <UploadIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </label>
          </Tooltip>
          <Tooltip title={removeTooltip}>
            <IconButton type="button" onClick={onRemove} size="small" sx={{ background: alpha('#EF4444', 0.15), color: '#F87171', '&:hover': { background: alpha('#EF4444', 0.25) } }}>
              <DeleteIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </MotionPaper>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title={isEdit ? 'Edit Company' : 'Create New Company'}
        subtitle={isEdit ? 'Update company information' : 'Register a company for partnerships'}
        icon={CompanyIcon} showBackButton onBackClick={() => navigate(-1)}
      />

      <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />
      <FeedbackMessage message={success} severity="success" onClose={() => setSuccess(null)}
        sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
      />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* ── 1. Company Details ─────────────────────────────────────── */}
          <SectionCard title="Company details" icon={<CompanyIcon />} delay={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <StyledField fullWidth required label="Company name" name="name" value={formData.name}
                onChange={handleNativeChange}
                error={fieldHasError('name')} helperText={fieldHelper('name')}
                InputProps={{ startAdornment: iconAdorn(NameIcon) }} />
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2.5 }}>
                <StyledField fullWidth required label="Contact number" name="contact_number" value={formData.contact_number}
                  onChange={handleNativeChange} sx={{ flex: 1 }}
                  error={fieldHasError('contact_number')} helperText={fieldHelper('contact_number')}
                  InputProps={{ startAdornment: iconAdorn(PhoneIcon) }} />
                <StyledField fullWidth required label="Company email" name="gmail" type="email" value={formData.gmail}
                  onChange={handleNativeChange} sx={{ flex: 1 }}
                  error={fieldHasError('gmail')} helperText={fieldHelper('gmail')}
                  InputProps={{ startAdornment: iconAdorn(EmailIcon) }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2.5, alignItems: { sm: 'flex-start' } }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <StyledField fullWidth label="Website" name="website_link" value={formData.website_link}
                    onChange={handleNativeChange}
                    error={fieldHasError('website_link')}
                    helperText={fieldHelper('website_link') || 'Paste the official site, then import public details'}
                    InputProps={{ startAdornment: iconAdorn(WebIcon) }} />
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    disabled={importLoading || !formData.website_link?.trim()}
                    onClick={handleImportFromWebsite}
                    startIcon={importLoading ? <CircularProgress size={16} color="inherit" /> : <ImportWebIcon />}
                    sx={{
                      alignSelf: 'flex-start',
                      borderRadius: '10px',
                      fontFamily: "'DM Sans', sans-serif",
                      textTransform: 'none',
                      borderColor: alpha(NEON, 0.35),
                      color: alpha(NEON, 0.9),
                      '&:hover': { borderColor: alpha(NEON, 0.6), background: alpha(NEON, 0.06) },
                      '&.Mui-disabled': { borderColor: alpha(NEON, 0.12), color: alpha(NEON, 0.25) },
                    }}
                  >
                    {importLoading ? 'Importing…' : 'Import from website'}
                  </Button>
                </Box>
                <StyledField fullWidth label="Location" name="location" value={formData.location}
                  onChange={handleNativeChange} sx={{ flex: 1 }}
                  error={fieldHasError('location')} helperText={fieldHelper('location')}
                  InputProps={{ startAdornment: iconAdorn(LocationIcon) }} />
              </Box>
            </Box>
          </SectionCard>

          {/* ── 2. Person in charge ────────────────────────────────────── */}
          <SectionCard title="Person in charge (external)" icon={<PersonIcon />} delay={0.04} accentNeon={NEON3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <StyledField fullWidth label="Name" name="person_in_charge_name" value={formData.person_in_charge_name}
                onChange={handleNativeChange}
                error={fieldHasError('person_in_charge_name')} helperText={fieldHelper('person_in_charge_name')}
                InputProps={{ startAdornment: iconAdorn(PersonIcon) }} />
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2.5 }}>
                <StyledField fullWidth label="Phone" name="person_in_charge_phone" value={formData.person_in_charge_phone}
                  onChange={handleNativeChange} sx={{ flex: 1 }}
                  error={fieldHasError('person_in_charge_phone')} helperText={fieldHelper('person_in_charge_phone')}
                  InputProps={{ startAdornment: iconAdorn(PhoneIcon) }} />
                <StyledField fullWidth label="Email" name="person_in_charge_email" type="email" value={formData.person_in_charge_email}
                  onChange={handleNativeChange} sx={{ flex: 1 }}
                  error={fieldHasError('person_in_charge_email')} helperText={fieldHelper('person_in_charge_email')}
                  InputProps={{ startAdornment: iconAdorn(EmailIcon) }} />
              </Box>
            </Box>
          </SectionCard>

          {/* ── 3. Social links ────────────────────────────────────────── */}
          <SectionCard title="Social links" icon={<SocialIcon />} delay={0.08} accentNeon={NEON3}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <StyledField fullWidth label="Facebook" name="facebook_path" value={formData.facebook_path}
                onChange={handleNativeChange}
                error={fieldHasError('facebook_path')} helperText={fieldHelper('facebook_path')}
                InputProps={{ startAdornment: iconAdorn(FacebookIcon) }} />
              <StyledField fullWidth label="Instagram" name="ig_path" value={formData.ig_path}
                onChange={handleNativeChange}
                error={fieldHasError('ig_path')} helperText={fieldHelper('ig_path')}
                InputProps={{ startAdornment: iconAdorn(InstagramIcon) }} />
              <StyledField fullWidth label="YouTube" name="youtube_path" value={formData.youtube_path}
                onChange={handleNativeChange}
                error={fieldHasError('youtube_path')} helperText={fieldHelper('youtube_path')}
                InputProps={{ startAdornment: iconAdorn(YouTubeIcon) }} />
              <StyledField fullWidth label="Other" name="other_path" value={formData.other_path}
                onChange={handleNativeChange}
                error={fieldHasError('other_path')} helperText={fieldHelper('other_path')}
                InputProps={{ startAdornment: iconAdorn(LinkIcon) }} />
            </Box>
          </SectionCard>

          {/* ── 4. Media ───────────────────────────────────────────────── */}
          <SectionCard title="Media" icon={<ImageIcon />} delay={0.12}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ ...monoSx(alpha(NEON, 0.65), '0.62rem'), mb: 1.5 }}>Company Logo</Typography>
                <input type="file" accept="image/jpeg,image/png,image/jpg,image/gif" onChange={handleLogoChange} style={{ display: 'none' }} id="logo-upload" />
                <ImagePreviewBlock preview={logoPreview} existingPath={existingLogo && !logoFile && !logoRemoved ? existingLogo : null} uploadId="logo-upload" onRemove={removeLogoImage} changeTooltip="Change Logo" removeTooltip="Remove Logo" altText="Logo preview" />
                {!logoPreview && !(isEdit && existingLogo && !logoFile && !logoRemoved) && (
                  <Box component="label" htmlFor="logo-upload" sx={{ display: 'block', ...mediaFrameSx }}>
                    <UploadZone id="logo-upload" label="Upload Company Logo" icon={AddPhotoIcon} hideFileInput onDrop={handleLogoDrop} onDragOver={preventDropDefaults} />
                  </Box>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ ...monoSx(alpha(NEON, 0.65), '0.62rem'), mb: 1.5 }}>Cover Image</Typography>
                <input type="file" accept="image/jpeg,image/png,image/jpg,image/gif" onChange={handleCoverChange} style={{ display: 'none' }} id="cover-upload" />
                <ImagePreviewBlock preview={coverPreview} existingPath={existingCover && !coverFile && !coverRemoved ? existingCover : null} uploadId="cover-upload" onRemove={removeCoverImage} changeTooltip="Change Cover" removeTooltip="Remove Cover" altText="Cover preview" />
                {!coverPreview && !(isEdit && existingCover && !coverFile && !coverRemoved) && (
                  <Box component="label" htmlFor="cover-upload" sx={{ display: 'block', ...mediaFrameSx }}>
                    <UploadZone id="cover-upload" label="Upload Cover Image" icon={AddPhotoIcon} hideFileInput onDrop={handleCoverDrop} onDragOver={preventDropDefaults} />
                  </Box>
                )}
              </Box>
            </Box>
          </SectionCard>

          {/* ── 5. Representative & Status ─────────────────────────────── */}
          <SectionCard title="Representative & status" icon={<PersonIcon />} delay={0.16}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2.5 }}>
                <SearchableSelect
                  required
                  multiple
                  label="Assigned members"
                  value={formData.user_ids}
                  onChange={(v) => handleChange('user_ids', v)}
                  options={userSelectOptions}
                  error={fieldHasError('user_ids')}
                  helperText={fieldHelper('user_ids')}
                  stackedTags
                  startIcon={<PersonIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                  sx={{ flex: 1 }}
                />
                <SearchableSelect
                  required
                  label="Pipeline status"
                  value={formData.status}
                  onChange={(v) => handleChange('status', v)}
                  options={PIPELINE_STATUS_OPTIONS}
                  error={fieldHasError('status')}
                  helperText={fieldHelper('status')}
                  startIcon={<FlagIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                  sx={{ flex: 1 }}
                />
              </Box>
              <StyledField fullWidth label="Description" name="description" value={formData.description}
                onChange={handleNativeChange} multiline rows={3}
                error={fieldHasError('description')} helperText={fieldHelper('description')}
                InputProps={{ startAdornment: topIconAdorn(NotesIcon) }} />
            </Box>
          </SectionCard>

          <SubmitActionsCard
            statusText={isEdit ? 'Save changes to update this company.' : 'Complete the form to add a new company.'}
            missingRequiredFields={isEdit ? null : [!formData.name?.trim() && 'Company Name', !formData.contact_number?.trim() && 'Contact Number', !formData.gmail?.trim() && 'Email', !formData.user_ids.length && 'Assigned member'].filter(Boolean)}
            readyStatusText="All required fields are complete. Ready to create company."
            cancelLabel="Cancel" onCancel={() => navigate(-1)} cancelDisabled={loading}
            submitLabel={isEdit ? 'Update Company' : 'Create Company'}
            submitDisabled={submitDisabled}
            loading={loading}
            submitStartIcon={<SaveIcon />}
            showSubmit
            submitType="submit"
            {...submitDraftProps}
          />

        </Box>
      </form>

      <ConfirmDeleteDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmUpdate}
        loading={loading} variant="edit" title="Confirm Update"
        itemType="company" itemName={formData.name}
        description="Please review your changes before saving. This will overwrite the existing company record."
        confirmLabel="Update Company" confirmColor="primary"
        confirmDisabled={!canSubmit}
      />
    </Container>
  );
};

export default CompanyForm;
